import { type ReactNode, useCallback, useState } from 'react'

import { useSidebar } from '@/components/ui/sidebar'
import { useOrgSlug } from '@/hooks/use-org-slug.hook'

import { SidebarNavGroupContainer } from './sidebar-nav-group.molecule'
import type { SidebarNavGroup } from './sidebar-nav-item.interface'
import {
  buildNavUrl,
  getHiddenCount,
  getVisibleItems,
  shouldShowToggle,
} from './sidebar-nav-menu-helpers.util'

interface SidebarNavMenuProps {
  groups: SidebarNavGroup[]
}

export const SidebarNavMenu = ({ groups }: SidebarNavMenuProps): ReactNode => {
  const orgSlug = useOrgSlug()
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  const toggleGroupExpanded = useCallback((groupLabel: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }))
  }, [])

  const buildUrl = (path: string): string => buildNavUrl(path, orgSlug)

  return (
    <>
      {groups.map((group) => {
        const isExpanded = !!expandedGroups[group.label]
        const visibleItemList = getVisibleItems(group, isExpanded)
        const showToggle = shouldShowToggle(group)
        const hiddenCount = getHiddenCount(group)

        return (
          <SidebarNavGroupContainer
            key={group.label}
            group={group}
            visibleItemList={visibleItemList}
            isExpanded={isExpanded}
            isCollapsed={isCollapsed}
            showToggle={showToggle}
            hiddenCount={hiddenCount}
            buildUrl={buildUrl}
            onToggle={() => toggleGroupExpanded(group.label)}
          />
        )
      })}
    </>
  )
}
